import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSocialAccounts, useBrands } from '../hooks/useApi';
import LoadingSpinner from '../components/LoadingSpinner';
import apiService from '../services/api';
import config from '../config/api';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const { user, updateUser } = useAuth();

  // Real profile data
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    timezone: 'Europe/Paris',
    language: 'English'
  });

  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);

  // Fetch real social accounts data
  const { data: socialAccountsData, loading: socialLoading, refetch: refetchSocial } = useSocialAccounts();
  const { data: brandsData } = useBrands();
  const [brandPickerFor, setBrandPickerFor] = useState(null);

  const [apiCredentials, setApiCredentials] = useState({
    openaiKey: '',
    openaiSecret: '••••••••••••••••'
  });

  // Load user profile data once on mount (fetch fresh from API to get timezone)
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const fresh = await apiService.getProfile();
        updateUser(fresh);
        setProfile({
          firstName: fresh.first_name || '',
          lastName: fresh.last_name || '',
          email: fresh.email || '',
          timezone: fresh.timezone || 'Europe/Paris',
          language: 'English'
        });
      } catch {
        // fallback to cached user
      }
      setProfileLoading(false);
    };

    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Transform social accounts data for display
  const getSocialConnections = () => {
    const platformsConfig = [
      {
        platform: 'LinkedIn',
        icon: 'fab fa-linkedin',
        color: 'text-blue-700',
        bgColor: 'bg-blue-50',
        key: 'LINKEDIN'
      },
      {
        platform: 'Facebook',
        icon: 'fab fa-facebook',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        key: 'FACEBOOK'
      },
      {
        platform: 'Instagram',
        icon: 'fab fa-instagram',
        color: 'text-pink-600',
        bgColor: 'bg-pink-50',
        key: 'INSTAGRAM'
      },
      {
        platform: 'X (Twitter)',
        icon: 'fab fa-twitter',
        color: 'text-gray-900',
        bgColor: 'bg-gray-50',
        key: 'X'
      },
      {
        platform: 'TikTok',
        icon: 'fab fa-tiktok',
        color: 'text-gray-900',
        bgColor: 'bg-gray-50',
        key: 'TIKTOK'
      },
      {
        platform: 'YouTube',
        icon: 'fab fa-youtube',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        key: 'YOUTUBE'
      }
    ];

    const socialAccounts = socialAccountsData?.results || [];

    return platformsConfig.map(config => {
      const connectedAccounts = socialAccounts.filter(account =>
        account.platform?.toUpperCase() === config.key
      );

      const hasConnectedAccounts = connectedAccounts.length > 0;
      const lastAccount = connectedAccounts.sort((a, b) =>
        new Date(b.updated_at) - new Date(a.updated_at)
      )[0];

      return {
        ...config,
        connected: hasConnectedAccounts,
        lastSync: lastAccount ? formatLastSync(lastAccount.updated_at) : null,
        accounts: connectedAccounts.map(account =>
          account.account_name || `${config.platform} Account`
        ),
        accountIds: connectedAccounts.map(account => account.id)
      };
    });
  };

  const formatLastSync = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile Settings', icon: 'fas fa-user' },
    { id: 'api', name: 'API Credentials', icon: 'fas fa-key' },
    { id: 'social', name: 'Social Connections', icon: 'fas fa-link' }
  ];

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleApiChange = (field, value) => {
    setApiCredentials(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileMessage(null);
    try {
      const updated = await apiService.updateProfile({
        first_name: profile.firstName,
        last_name: profile.lastName,
        email: profile.email,
        timezone: profile.timezone
      });
      updateUser(updated);
      setProfileMessage({ type: 'success', text: 'Profile saved successfully!' });
    } catch (error) {
      console.error('Failed to save profile:', error);
      setProfileMessage({ type: 'error', text: error.message || 'Failed to save profile' });
    } finally {
      setProfileSaving(false);
    }
  };

  const oauthEndpoints = {
    'LINKEDIN': config.endpoints.OAUTH_LINKEDIN_INIT,
    'X': config.endpoints.OAUTH_X_INIT,
  };

  const startOAuthFlow = (brandId, platformKey) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('You must be logged in to connect a platform.');
      return;
    }
    const endpoint = oauthEndpoints[platformKey];
    const params = new URLSearchParams({ brand_id: brandId, token });
    window.location.href = `${config.BACKEND_URL}${endpoint}?${params.toString()}`;
  };

  const handleConnectPlatform = (platform, platformKey) => {
    if (!oauthEndpoints[platformKey]) {
      alert(`${platform} OAuth integration coming soon!`);
      return;
    }

    const brands = brandsData?.results || [];
    if (brands.length === 0) {
      alert('Please create a Brand first before connecting a platform.');
      return;
    }
    if (brands.length === 1) {
      startOAuthFlow(brands[0].id, platformKey);
    } else {
      setBrandPickerFor({ platform, platformKey });
    }
  };

  const handleDisconnectPlatform = async (connection) => {
    if (connection.accountIds.length === 0) return;

    if (window.confirm(`Disconnect all ${connection.platform} accounts?`)) {
      try {
        await Promise.all(
          connection.accountIds.map(accountId =>
            apiService.deleteSocialAccount(accountId)
          )
        );
        refetchSocial();
      } catch (error) {
        console.error('Failed to disconnect:', error);
        alert('Failed to disconnect. Please try again.');
      }
    }
  };

  const [syncing, setSyncing] = useState(null);

  const handleSyncPlatform = async (connection) => {
    // Find the brand that has this connected account and refresh analytics
    setSyncing(connection.key);
    try {
      // Get the first brand that has this platform connected
      const socialAccounts = socialAccountsData?.results || [];
      const account = socialAccounts.find(a => a.platform?.toUpperCase() === connection.key);
      if (account && account.brand) {
        await apiService.refreshAnalytics(account.brand);
      }
      refetchSocial();
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setSyncing(null);
    }
  };

  const renderProfileSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium font-heading text-gray-900 mb-4">Personal Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name
            </label>
            <input
              type="text"
              value={profile.firstName}
              onChange={(e) => handleProfileChange('firstName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-accent-300 focus:border-accent-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name
            </label>
            <input
              type="text"
              value={profile.lastName}
              onChange={(e) => handleProfileChange('lastName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-accent-300 focus:border-accent-300"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={profile.email}
              onChange={(e) => handleProfileChange('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-accent-300 focus:border-accent-300"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium font-heading text-gray-900 mb-4">Preferences</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Timezone
            </label>
            <select
              value={profile.timezone}
              onChange={(e) => handleProfileChange('timezone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-accent-300 focus:border-accent-300"
            >
              <option value="Europe/Paris">Europe/Paris (CET, UTC+1)</option>
              <option value="Europe/London">Europe/London (GMT, UTC+0)</option>
              <option value="America/New_York">America/New_York (EST, UTC-5)</option>
              <option value="America/Chicago">America/Chicago (CST, UTC-6)</option>
              <option value="America/Denver">America/Denver (MST, UTC-7)</option>
              <option value="America/Los_Angeles">America/Los_Angeles (PST, UTC-8)</option>
              <option value="Asia/Tokyo">Asia/Tokyo (JST, UTC+9)</option>
              <option value="Asia/Dubai">Asia/Dubai (GST, UTC+4)</option>
              <option value="Australia/Sydney">Australia/Sydney (AEST, UTC+10)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Language
            </label>
            <select
              value={profile.language}
              onChange={(e) => handleProfileChange('language', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-accent-300 focus:border-accent-300"
            >
              <option value="English">English</option>
              <option value="French">French</option>
              <option value="German">German</option>
              <option value="Spanish">Spanish</option>
            </select>
          </div>
        </div>
      </div>

      {profileMessage && (
        <div className={`p-3 rounded-xl text-sm ${
          profileMessage.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          <i className={`mr-2 ${profileMessage.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'}`}></i>
          {profileMessage.text}
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleSaveProfile}
          disabled={profileSaving}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-accent-900 bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-300 disabled:opacity-50"
        >
          {profileSaving ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </div>
  );

  const renderApiSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium font-heading text-gray-900 mb-4">API Configuration</h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure API credentials for AI-powered content generation and analytics
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              OpenAI API Key
            </label>
            <div className="relative">
              <input
                type="text"
                value={apiCredentials.openaiKey}
                onChange={(e) => handleApiChange('openaiKey', e.target.value)}
                placeholder="sk-..."
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-accent-300 focus:border-accent-300 pr-10"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <i className="fas fa-key text-gray-400"></i>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Used for AI content generation and optimization
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              OpenAI Organization (Optional)
            </label>
            <div className="relative">
              <input
                type="password"
                value={apiCredentials.openaiSecret}
                onChange={(e) => handleApiChange('openaiSecret', e.target.value)}
                placeholder="org-..."
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-accent-300 focus:border-accent-300 pr-10"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <i className="fas fa-shield-alt text-gray-400"></i>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Organization ID for enterprise accounts
            </p>
          </div>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <i className="fas fa-exclamation-triangle text-yellow-400"></i>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium font-heading text-yellow-800">
              Keep your API keys secure
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Never share your API keys with others. Socialight encrypts and securely stores your credentials.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button className="inline-flex items-center px-4 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-300">
          Test Connection
        </button>
        <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-accent-900 bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-300">
          Save Credentials
        </button>
      </div>
    </div>
  );

  const renderSocialConnections = () => {
    if (socialLoading) {
      return <LoadingSpinner text="Loading social connections..." />;
    }

    const socialConnections = getSocialConnections();

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium font-heading text-gray-900 mb-4">Social Media Platforms</h3>
          <p className="text-sm text-gray-600 mb-6">
            Connect your social media accounts to manage posts across multiple platforms
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {socialConnections.map((connection) => (
          <div key={connection.platform} className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className={`p-2 rounded-xl ${connection.bgColor}`}>
                  <i className={`${connection.icon} text-xl ${connection.color}`}></i>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-gray-900">{connection.platform}</h4>
                  {connection.connected && (
                    <p className="text-xs text-gray-500">
                      Last sync: {connection.lastSync}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                {connection.connected ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <i className="fas fa-check-circle mr-1"></i>
                    Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <i className="fas fa-times-circle mr-1"></i>
                    Disconnected
                  </span>
                )}
              </div>
            </div>

            {connection.connected && connection.accounts.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-700 mb-2">Connected Accounts:</p>
                <div className="space-y-1">
                  {connection.accounts.map((account, index) => (
                    <div key={index} className="flex items-center text-xs text-gray-600">
                      <i className="fas fa-circle text-green-500 mr-2" style={{ fontSize: '4px' }}></i>
                      {account}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex space-x-2">
              {connection.connected ? (
                <>
                  <button
                    onClick={() => handleSyncPlatform(connection)}
                    disabled={syncing === connection.key}
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-200 text-xs font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    <i className={`fas fa-sync-alt mr-1 ${syncing === connection.key ? 'fa-spin' : ''}`}></i>
                    {syncing === connection.key ? 'Syncing...' : 'Sync Now'}
                  </button>
                  <button
                    onClick={() => handleDisconnectPlatform(connection)}
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-red-200 text-xs font-medium rounded-xl text-red-700 bg-white hover:bg-red-50"
                  >
                    <i className="fas fa-unlink mr-1"></i>
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleConnectPlatform(connection.platform, connection.key)}
                  className="w-full inline-flex justify-center items-center px-3 py-2 border border-transparent text-xs font-medium rounded-xl text-accent-900 bg-accent-600 hover:bg-accent-700"
                >
                  <i className="fas fa-link mr-1"></i>
                  Connect {connection.platform}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <i className="fas fa-info-circle text-blue-400"></i>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium font-heading text-blue-800">
              Need help connecting a platform?
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Check our <button type="button" className="underline hover:text-blue-600 text-blue-700 cursor-pointer">integration guide</button> for step-by-step instructions on connecting each social media platform.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold font-heading text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account settings and platform integrations
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center
                ${activeTab === tab.id
                  ? 'border-accent-500 text-accent-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <i className={`${tab.icon} mr-2`}></i>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-6">
          {activeTab === 'profile' && renderProfileSettings()}
          {activeTab === 'api' && renderApiSettings()}
          {activeTab === 'social' && renderSocialConnections()}
        </div>
      </div>

      {/* Brand Picker Modal */}
      {brandPickerFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-medium font-heading text-gray-900 mb-2">
              Connect {brandPickerFor.platform}
            </h3>
            <p className="text-sm text-gray-600 mb-4">Select a brand to connect:</p>
            <div className="space-y-2 mb-4">
              {(brandsData?.results || []).map((brand) => (
                <button
                  key={brand.id}
                  onClick={() => {
                    setBrandPickerFor(null);
                    startOAuthFlow(brand.id, brandPickerFor.platformKey);
                  }}
                  className="w-full text-left px-4 py-3 border border-gray-200 rounded-xl hover:bg-accent-50 hover:border-accent-300 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900">{brand.name}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setBrandPickerFor(null)}
              className="w-full px-4 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
